/**
 * ⚡ AetherAshPetBlocks — the four Aether & Ash blocks that live on
 * the Pet tab (owner directive Oct 2026):
 *   1. Collection Stats (headline card)
 *   2. Collection Breadth
 *   3. How Combat Power Is Built
 *   4. The Ladder (static tier preview — leaderboard version lives in
 *      the Leaderboards page as its own tab).
 *
 * Fetches `/aether-and-ash/sheet` once per mount. Fails silently if the
 * sheet is unavailable (e.g., no hero class picked yet) so the Pet page
 * continues to render for brand-new accounts.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, Sparkles, Crown, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

const STAT_LABEL = {
  patk:        "P. ATK",
  matk:        "M. ATK",
  pdef:        "P. DEF",
  mdef:        "M. DEF",
  block:       "Block",
  charisma:    "Charisma",
  base_stats:  "Base combat stats",
  base_outfit: "Base outfit",
  level:       "Level",
};

// 🪜 40-rung ladder — owner spec Oct 8 2026. 8 majors × 5 subs. Ceiling 10M.
// Each row is [min_threshold, major, sub]. The "min_threshold" is the FIRST
// value that places you on that rung — spec written as inclusive ranges.
const LADDER = [
  [1,          "Common Bird",       "I"],
  [201,        "Common Bird",       "II"],
  [401,        "Common Bird",       "III"],
  [601,        "Common Bird",       "IV"],
  [801,        "Common Bird",       "V"],
  [1_001,      "Ember",             "I"],
  [1_801,      "Ember",             "II"],
  [2_601,      "Ember",             "III"],
  [3_401,      "Ember",             "IV"],
  [4_201,      "Ember",             "V"],
  [5_001,      "Ash Walker",        "I"],
  [9_001,      "Ash Walker",        "II"],
  [13_001,     "Ash Walker",        "III"],
  [17_001,     "Ash Walker",        "IV"],
  [21_001,     "Ash Walker",        "V"],
  [25_001,     "Flame Bound",       "I"],
  [45_001,     "Flame Bound",       "II"],
  [65_001,     "Flame Bound",       "III"],
  [85_001,     "Flame Bound",       "IV"],
  [105_001,    "Flame Bound",       "V"],
  [125_001,    "Pyre Keeper",       "I"],
  [225_001,    "Pyre Keeper",       "II"],
  [325_001,    "Pyre Keeper",       "III"],
  [425_001,    "Pyre Keeper",       "IV"],
  [525_001,    "Pyre Keeper",       "V"],
  [625_001,    "Aether Touched",    "I"],
  [1_100_001,  "Aether Touched",    "II"],
  [1_575_001,  "Aether Touched",    "III"],
  [2_050_001,  "Aether Touched",    "IV"],
  [2_525_001,  "Aether Touched",    "V"],
  [3_000_001,  "Phoenix Ascendant", "I"],
  [3_700_001,  "Phoenix Ascendant", "II"],
  [4_400_001,  "Phoenix Ascendant", "III"],
  [5_100_001,  "Phoenix Ascendant", "IV"],
  [5_800_001,  "Phoenix Ascendant", "V"],
  [6_500_001,  "Omni-Lit",          "I"],
  [7_200_001,  "Omni-Lit",          "II"],
  [7_900_001,  "Omni-Lit",          "III"],
  [8_600_001,  "Omni-Lit",          "IV"],
  [9_300_001,  "Omni-Lit",          "V"],
];
const POWER_CEILING = 10_000_000;

const MAJOR_TINT = {
  "Common Bird":       "text-slate-200",
  "Ember":             "text-orange-300",
  "Ash Walker":        "text-zinc-200",
  "Flame Bound":       "text-amber-300",
  "Pyre Keeper":       "text-rose-300",
  "Aether Touched":    "text-fuchsia-300",
  "Phoenix Ascendant": "text-indigo-300",
  "Omni-Lit":          "text-yellow-200",
};

function CollectionChip({ icon, label, count }) {
  return (
    <div
      className="rounded-lg border border-amber-900/40 bg-gradient-to-br from-amber-950/40 to-black p-2.5 flex items-center gap-2"
      data-testid={`pet-aa-coll-${label.toLowerCase().replace(/\s+/g,'-')}`}
    >
      <span className="text-2xl leading-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-amber-300/70 truncate">{label}</div>
        <div className="font-mono text-sm font-bold text-amber-100 tabular-nums">
          {fmt(count)}
        </div>
      </div>
    </div>
  );
}

export default function AetherAshPetBlocks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guildBusy, setGuildBusy] = useState(false);

  async function loadSheet() {
    try {
      const { data } = await axios.get(`${API}/aether-and-ash/sheet`);
      setData(data);
    } catch {
      // Silent — Aether & Ash is optional context on the Pet page.
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/aether-and-ash/sheet`);
        if (!cancelled) setData(data);
      } catch {
        // Silent — Aether & Ash is optional context on the Pet page.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function toggleGuildTerm(nextOptIn) {
    if (guildBusy) return;
    setGuildBusy(true);
    try {
      const r = await axios.post(
        `${API}/aether-and-ash/guild-term-opt-in`,
        { opted_in: !!nextOptIn },
      );
      toast.success(
        nextOptIn
          ? "🤝 Guild Connection ON — contributing to Battle Power."
          : "🤝 Guild Connection OFF — term removed from BP.",
        {
          description: `Battle Power: ${Number(r.data?.battle_power || 0).toLocaleString()} · Guild Term: ${Number(r.data?.guild_term || 0).toLocaleString()}`,
          duration: 4000,
        },
      );
      await loadSheet();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle Guild Term.");
    } finally {
      setGuildBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="mb-6 text-center text-neutral-600 text-xs py-6" data-testid="pet-aa-loading">
        Loading Aether &amp; Ash…
      </div>
    );
  }
  if (!data) return null;

  // 🪄 Feb 2026 — locked Aether + Ash split. The headline is Aether + Ash;
  // `combat_power` / `collection_power` keys are kept for backward compat.
  const aether = data?.aether_power     || { total: 0, breakdown: {}, counts: {} };
  const ash    = data?.ash_power        || { total: 0, breakdown: {}, counts: {} };
  const coll   = data?.collection       || { counts: {}, note: "" };
  const tier   = data?.tier             || {};
  const total  = data?.total_power      || 0;
  // 🤝 Guild → BP opt-in term (Feb 2026). Always shown so players can see
  // and toggle the mechanic; current contribution comes straight off the sheet.
  const guild  = data?.guild_term       || { total: 0, opted_in: false, guild_id: null,
                                             guild_name: null, guild_level: 0, K: 0 };

  return (
    <div className="mb-6" data-testid="pet-aa-blocks">
      {/* 📊 Battle Power headline — Aether + Ash summary. */}
      <div
        className="rounded-xl border border-amber-800/40 bg-gradient-to-br from-amber-950/40 via-black to-neutral-950 p-4 mb-6"
        data-testid="pet-aa-collection-stats"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/70 mb-2">
          ⚡ Aether &amp; Ash · Battle Power
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300/70">Battle Power</div>
            <div
              className="font-mono text-2xl font-black tabular-nums bg-gradient-to-b from-amber-200 via-amber-400 to-rose-500 bg-clip-text text-transparent"
              data-testid="pet-aa-total-power"
            >
              {fmt(total)}
            </div>
            <div className="mt-0.5 text-[10px] text-amber-200/70 uppercase tracking-widest">
              {tier.label || "Common Bird I"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-300/70">Aether</div>
            <div className="font-mono text-xl font-bold text-sky-200 tabular-nums" data-testid="pet-aa-aether-power">
              {fmt(aether.total)}
            </div>
            <div className="text-[10px] text-neutral-500">Level · Skills</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-300/70">Ash</div>
            <div className="font-mono text-xl font-bold text-orange-200 tabular-nums" data-testid="pet-aa-ash-power">
              {fmt(ash.total)}
            </div>
            <div className="text-[10px] text-neutral-500">Base stats · Base outfit</div>
          </div>
        </div>
        <div className="mt-3 text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-900 pt-2">
          <span className="text-sky-300/90 font-semibold">Aether</span> = Level × 100 + Skills × 50
          &nbsp;·&nbsp;
          <span className="text-orange-300/90 font-semibold">Ash</span> = base combat stats + base outfit (1 stat point = 1 Ash)
        </div>

        {/* 🤝 Guild → BP opt-in row (owner directive Feb 2026) */}
        <div
          className="mt-3 rounded-lg border border-emerald-900/40 bg-emerald-950/20 p-3"
          data-testid="pet-aa-guild-term"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-300/80">
                <Users className="h-3 w-3" /> Guild Connection
                {guild.opted_in && (
                  <span
                    className="ml-1 text-[9px] uppercase tracking-widest bg-emerald-500/20 text-emerald-200 px-1.5 py-0.5 rounded"
                    data-testid="pet-aa-guild-term-chip"
                  >
                    opted in
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="font-mono text-base font-bold text-emerald-200 tabular-nums"
                  data-testid="pet-aa-guild-term-value"
                >
                  +{fmt(guild.total)}
                </span>
                <span className="text-[10px] text-neutral-500">
                  guild_level × K
                </span>
              </div>
              <div className="mt-0.5 text-[10px] text-neutral-500 truncate">
                {guild.guild_id
                  ? <>Guild {guild.guild_name || guild.guild_id} · Lv {fmt(guild.guild_level)} × {fmt(guild.K)}</>
                  : <>Join a guild to earn the term — K = {fmt(guild.K)} per guild level.</>
                }
              </div>
            </div>
            <button
              data-testid="pet-aa-guild-term-toggle"
              disabled={guildBusy}
              onClick={() => toggleGuildTerm(!guild.opted_in)}
              className={`shrink-0 self-center px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                guild.opted_in
                  ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-100 hover:bg-emerald-500/30"
                  : "bg-neutral-900/60 border-neutral-700 text-neutral-300 hover:bg-neutral-800"
              }`}
            >
              {guildBusy ? "…" : guild.opted_in ? "On" : "Off"}
            </button>
          </div>
        </div>
      </div>

      {/* 🪙 Collection Breadth — display-only, no longer feeds Battle Power. */}
      <section className="mb-6">
        <h2 className="font-display text-2xl text-white mb-3 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-fuchsia-300" /> Collection Breadth
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="pet-aa-collection-breadth">
          <CollectionChip icon="💎" label="Soul Crystals"       count={coll.counts?.soul_crystals} />
          <CollectionChip icon="🃏" label="Battle Card Shards"  count={coll.counts?.battle_card_shards} />
          <CollectionChip icon="🦄" label="Mounts"              count={coll.counts?.mounts} />
          <CollectionChip icon="🏅" label="Badges"              count={coll.counts?.badges} />
          <CollectionChip icon="🪶" label="Phoenix Feathers"    count={coll.counts?.phoenix_feathers} />
          <CollectionChip icon="🧩" label="Rush Shards"         count={coll.counts?.rush_shards} />
          <CollectionChip icon="💠" label="Mother Crystals (equipped)" count={coll.counts?.mother_crystals_equipped} />
        </div>
        <p className="mt-2 text-[11px] text-neutral-500 italic">
          {coll.note || "Lifetime display only — Collection Breadth does not feed Battle Power."}
        </p>
      </section>

      {/* 🧮 How Ash Is Built */}
      <section className="mb-6">
        <h2 className="font-display text-2xl text-white mb-3 flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-300" /> How Ash Is Built
        </h2>
        <div
          className="rounded-xl border border-neutral-800 bg-black/50 p-4"
          data-testid="pet-aa-combat-breakdown"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
            {Object.entries(ash.breakdown || {})
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-neutral-900 pb-1">
                  <span className="text-xs uppercase tracking-widest text-neutral-400">
                    {STAT_LABEL[k] || k.replace(/_/g, " ")}
                  </span>
                  <span className="font-mono text-sm font-semibold text-orange-200 tabular-nums">
                    {fmt(v)}
                  </span>
                </div>
              ))}
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-sky-900/40 bg-sky-950/20 p-4" data-testid="pet-aa-aether-breakdown">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300/70 mb-2">⚡ Aether breakdown</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex items-center justify-between border-b border-sky-900/40 pb-1">
              <span className="text-xs uppercase tracking-widest text-neutral-400">Level × 100</span>
              <span className="font-mono text-sm font-semibold text-sky-200 tabular-nums">
                {fmt(aether.breakdown?.level_term)}
                <span className="ml-1 text-[10px] text-sky-500/70">(Lv {fmt(aether.counts?.level)})</span>
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-sky-900/40 pb-1">
              <span className="text-xs uppercase tracking-widest text-neutral-400">Skills × 50</span>
              <span className="font-mono text-sm font-semibold text-sky-200 tabular-nums">
                {fmt(aether.breakdown?.skills_term)}
                <span className="ml-1 text-[10px] text-sky-500/70">({fmt(aether.counts?.skills)} ranks)</span>
              </span>
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
          Battle Power = <span className="text-sky-300/90">Aether</span> + <span className="text-orange-300/90">Ash</span>.
          Aether rewards <span className="text-sky-300/80">levelling and skill investment</span>.
          Ash is the straight sum of your <span className="text-orange-300/80">base combat stats + the stat points on your base outfit</span> — 1 stat point = 1 Ash.
        </p>
      </section>

      {/* 🏆 The Ladder — static tier preview. Global leaderboard version
          lives in /leaderboards as its own tab. */}
      <section className="mb-6">
        <h2 className="font-display text-3xl text-white mb-3 flex items-center gap-2">
          <Crown className="h-6 w-6 text-amber-300" /> The Ladder
        </h2>
        <div
          className="rounded-2xl border-2 border-amber-700/50 bg-gradient-to-b from-amber-950/30 via-black/60 to-black p-4 shadow-[0_0_60px_-20px_rgba(251,191,36,0.5)]"
          data-testid="pet-aa-ladder"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/70 mb-3">
            8 Tiers × 5 Sub-tiers · Ceiling {fmt(POWER_CEILING)}
          </div>
          {LADDER.map(([threshold, major, sub], i) => {
            const nextThreshold = i + 1 < LADDER.length ? LADDER[i + 1][0] : POWER_CEILING + 1;
            const reached = total >= threshold;
            const current = total >= threshold && total < nextThreshold;
            const tint = MAJOR_TINT[major] || "text-neutral-300";
            const label = `${major} ${sub}`;
            return (
              <div
                key={label}
                className={`flex items-center justify-between py-1 border-b border-neutral-900 last:border-b-0 transition-colors ${
                  current ? "bg-amber-500/10" : ""
                } ${current ? tint : reached ? "text-neutral-300" : "text-neutral-600"}`}
                data-testid={`pet-aa-tier-${label.toLowerCase().replace(/\s+/g,'-')}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs w-4 text-center">{reached ? "🔥" : "·"}</span>
                  <span className={`text-sm truncate ${current ? "font-black uppercase tracking-widest" : "font-semibold"}`}>
                    {label}
                  </span>
                  {current && <span className="text-[9px] uppercase tracking-widest bg-amber-500/20 text-amber-200 px-1.5 py-0.5 rounded whitespace-nowrap">You are here</span>}
                </div>
                <span className="font-mono text-[11px] tabular-nums text-neutral-500 pl-2">
                  {fmt(threshold)}+
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-neutral-500 leading-relaxed">
          Climb by adding gear, mounts, pets, crystals, and badges. See where you stand globally in
          <a href="/leaderboards" className="underline text-amber-300/80 hover:text-amber-200"> Leaderboards → ⚡ The Ladder</a>.
        </p>
      </section>
    </div>
  );
}
